import { css } from '@emotion/css';
import { DataFrameType, LoadingState, type GrafanaTheme2, type ValueMapping } from '@grafana/data';
import {
  SceneObjectBase,
  type SceneComponentProps,
  type SceneDataProvider,
  type SceneObjectState,
  type VizPanel,
  type VizPanelState,
} from '@grafana/scenes';
import { useStyles2, type VizLegendOptions } from '@grafana/ui';
import { isEqual } from 'lodash';
import React from 'react';

import { getTrailFor } from 'shared/utils/utils';

import { type LabelMatcher } from './buildQueryExpression';
import { EventPanelTypeChanged } from './components/EventPanelTypeChanged';
import { SelectAction } from './components/SelectAction';
import { getPreferredConfigForMetric } from './config/getPreferredConfigForMetric';
import { PANEL_HEIGHT } from './config/panel-heights';
import { type PrometheusFunction } from './config/promql-functions';
import { QUERY_RESOLUTION } from './config/query-resolutions';
import { getPanelTypeForMetricSync } from './matchers/getPanelTypeForMetric';
import { isClassicHistogramMetric } from './matchers/isClassicHistogramMetric';
import { type PanelType } from './types/available-panel-types';
import { panelBuilder } from './types/panelBuilder';

/* Panel config */

type HeaderActionAndMenuArgs = { metric: string; panelConfig: PanelConfig };

export type PanelConfig = {
  type: PanelType;
  title: string;
  height: PANEL_HEIGHT;
  headerActions: (headerActionsArgs: HeaderActionAndMenuArgs) => VizPanelState['headerActions'];
  fixedColorIndex?: number;
  description?: string;
  menu?: (menuArgs: HeaderActionAndMenuArgs) => VizPanelState['menu'];
  legend?: Partial<VizLegendOptions>;
  mappings?: ValueMapping[];
  behaviors?: VizPanelState['$behaviors'];
};

export type PanelOptions = {
  type?: PanelConfig['type'];
  height?: PanelConfig['height'];
  fixedColorIndex?: PanelConfig['fixedColorIndex'];
  title?: PanelConfig['title'];
  description?: PanelConfig['description'];
  headerActions?: PanelConfig['headerActions'];
  menu?: PanelConfig['menu'];
  legend?: PanelConfig['legend'];
  mappings?: PanelConfig['mappings'];
  behaviors?: PanelConfig['behaviors'];
};

/* Query config */

export type QueryDefs = Array<{
  fn: PrometheusFunction;
  params?: Record<string, any>;
}>;

export type QueryConfig = {
  resolution: QUERY_RESOLUTION;
  labelMatchers: LabelMatcher[];
  addIgnoreUsageFilter: boolean;
  addExtremeValuesFiltering?: boolean;
  groupBy?: string;
  queries?: QueryDefs;
  data?: SceneDataProvider;
};

export type QueryOptions = {
  resolution?: QueryConfig['resolution'];
  labelMatchers?: QueryConfig['labelMatchers'];
  groupBy?: string;
  queries?: QueryDefs;
  data?: QueryConfig['data'];
};

/* GmdVizPanelState */

export type HistogramType = 'native' | 'classic' | 'none';

interface GmdVizPanelState extends SceneObjectState {
  metric: string;
  histogramType: HistogramType;
  panelConfig: PanelConfig;
  queryConfig: QueryConfig;
  body?: VizPanel;
}

export class GmdVizPanel extends SceneObjectBase<GmdVizPanelState> {
  constructor({
    key,
    metric,
    panelOptions,
    queryOptions,
    discardUserPrefs,
  }: {
    key?: string;
    metric: GmdVizPanelState['metric'];
    panelOptions?: PanelOptions;
    queryOptions?: QueryOptions;
    discardUserPrefs?: boolean;
  }) {
    const histogramType = isClassicHistogramMetric(metric) ? 'classic' : 'none';
    const prefConfig = discardUserPrefs ? undefined : getPreferredConfigForMetric(metric);

    super({
      key,
      metric,
      histogramType,
      panelConfig: {
        // we want a panel type to be able to render the panel as soon as possible after activation
        // so we don't determine if the metric is a native histogram here because it's an async process that will be done in onActivate()
        type: panelOptions?.type || getPanelTypeForMetricSync(metric, histogramType),
        title: metric,
        height: PANEL_HEIGHT.M,
        headerActions: ({ metric }) => [new SelectAction({ metric })],
        ...panelOptions,
        ...prefConfig?.panelOptions,
      },
      queryConfig: {
        resolution: QUERY_RESOLUTION.MEDIUM,
        labelMatchers: [],
        addIgnoreUsageFilter: true,
        ...queryOptions,
        ...prefConfig?.queryOptions,
      },
      body: undefined,
    });

    this.addActivationHandler(() => {
      this.onActivate(Boolean(panelOptions?.type || prefConfig?.panelOptions.type));
    });
  }

  private async onActivate(discardPanelTypeUpdates: boolean) {
    const { metric, panelConfig } = this.state;

    this.buildVizPanel();

    this.subscribeToStateChanges(discardPanelTypeUpdates);
    this.subscribeToEvents();

    const isNativeHistogram = await getTrailFor(this).isNativeHistogram(metric);
    if (isNativeHistogram) {
      this.setState({
        histogramType: 'native',
        panelConfig: discardPanelTypeUpdates
          ? panelConfig
          : { description: 'Native Histogram ', ...panelConfig, type: 'heatmap' },
      });
    }
  }

  private subscribeToStateChanges(discardPanelTypeUpdates: boolean) {
    const { histogramType, body, panelConfig } = this.state;

    // in addition to using the metadata fetched in src/helpers/MetricDatasourceHelper.ts to determine if the metric is a native histogram or not,
    // we give another chance to display it properly by looking into the data frame type received
    if (!discardPanelTypeUpdates && histogramType === 'none') {
      const bodySub = (body?.state.$data as SceneDataProvider)?.subscribeToState((newState) => {
        if (newState.data?.state !== LoadingState.Done) {
          return;
        }

        const dataFrameType = newState.data.series[0]?.meta?.type;
        if (!dataFrameType) {
          return;
        }

        if (dataFrameType === DataFrameType.HeatmapCells) {
          this.setState({
            histogramType: 'native',
            panelConfig: { description: 'Native Histogram ', ...panelConfig, type: 'heatmap' },
          });
        }

        bodySub.unsubscribe();
      });

      this._subs.add(bodySub);
    }

    this.subscribeToState((newState, prevState) => {
      if (
        newState.histogramType !== prevState.histogramType ||
        !isEqual(newState.panelConfig, prevState.panelConfig) ||
        !isEqual(newState.queryConfig, prevState.queryConfig)
      ) {
        this.buildVizPanel();
      }
    });
  }

  private subscribeToEvents() {
    this.subscribeToEvent(EventPanelTypeChanged, (event) => {
      this.setState({
        panelConfig: {
          ...this.state.panelConfig,
          type: event.payload.panelType,
        },
      });
    });
  }

  private buildVizPanel() {
    const { metric, panelConfig, queryConfig, histogramType } = this.state;

    this.setState({
      body: panelBuilder.buildVizPanel({
        metric,
        panelConfig,
        queryConfig,
        histogramType,
      }),
    });
  }

  public update(panelOptions: PanelOptions, queryOptions: QueryOptions) {
    const { panelConfig, queryConfig } = this.state;

    this.setState({
      panelConfig: {
        ...panelConfig,
        ...panelOptions,
      },
      queryConfig: {
        ...queryConfig,
        ...queryOptions,
      },
    });
  }

  public static readonly Component = ({ model }: SceneComponentProps<GmdVizPanel>) => {
    const { body, panelConfig } = model.useState();
    const styles = useStyles2(getStyles, panelConfig.height);

    return (
      <div className={styles.container} data-testid="gmd-vizpanel">
        {body && <body.Component model={body} />}
      </div>
    );
  };
}

function getStyles(theme: GrafanaTheme2, height: PANEL_HEIGHT) {
  return {
    container: css`
      width: 100%;
      height: ${height}px;
    `,
  };
}
